<template>
    <div class="px-3 py-3 px-md-4 ai-search-bar" :class="barCssClass">
        <template v-if="error">
            {{ error }} <ui-button @click="requestFresh" size="sm">Try Again</ui-button>
        </template>
        <template v-else-if="aiProfileData">
            <template v-if="aiProfileOptions">
                <container>
                    <row>
                        <column :md="12" :lg="8">
                            <div class="clickable d-flex align-items-baseline justify-content-between" @click="showOptions = !showOptions">
                                <h6 class="m-0 pe-2 pe-md-5 text-truncate"><em>{{ selectedOption ? selectedOption.label : 'Search Based on Your Personal Tastes' }}</em></h6>
                                <down-arrow-icon :size="18" style="transition: transform 0.25s;" :style="`transform: rotate(${showOptions ? 180 : 0}deg);`" />
                            </div>
                        </column>
                        <column v-if="!uiStore.isMobile" :md="12" :lg="16" class="d-flex align-items-center justify-content-end pt-1">
                            <div class="text-decoration-underline clickable text-secondary small" @click="goToNewProfile">
                                Create a New Aesthetic Profile
                            </div>
                            <div class="rounded rounded-pill px-1 small ms-md-3 clickable" style="background-color:rgba(255, 255, 255, 0.25); width:40px; padding-top:1px; padding-bottom:1px;" @click="checkboxClick">
                                <i :class="selectedOption ? 'fas fa-check-circle' : 'far fa-circle text-muted'" style="transition: margin 0.25s;" :style="selectedOption ? 'margin-left: 17px;' : 'margin-left: 0px;'"></i>
                            </div>
                        </column>
                        <transition name="v-slide-transition-5">

                            <column v-if="showOptions" class="overflow-auto max-vh-40 border-top pt-2 mt-2">
                                <div v-if="uiStore.isMobile" class="py-2 clickable" @click="goToNewProfile">
                                    <h6 class="m-0 pe-5 text-truncate">Create a New Aesthetic Profile</h6>
                                </div>
                                <div class="py-2 clickable" @click="setOption">
                                    <h6 class="m-0 pe-5 text-truncate" :class="selectedOption ? 'text-muted' : null">None</h6>
                                </div>
                                <div v-for="(option, index) in aiProfileOptions" :key="`ai-profile-option-${option.value}-${index}`" class="py-2 clickable" @click="() => setOption(option)">
                                    <h6 class="m-0 pe-5 text-truncate" :class="option.value == localValue ? null : 'text-muted'"><em>{{ option.label }}</em></h6>
                                </div>
                            </column>
                        </transition>
                    </row>
                </container>
            </template>
            <template v-else>
                <h6 v-if="uiStore.isMobile" class="m-0 text-center clickable" @click="goToNewProfile">
                    <em>Create a New Aesthetic Profile</em>
                </h6>
                <div v-else class="d-flex align-items-center justify-content-between clickable" @click="goToNewProfile">
                    <h6 class="m-0"><em>Search Based on Your Personal Tastes</em></h6>
                    <div class="text-decoration-underline text-secondary small">Create a New Aesthetic Profile</div>
                </div>
            </template>
        </template>
        <div v-else class="small">
            <spinner size="sm" /> Loading Aesthetic Profile Options...
        </div>
    </div>
</template>

<script type="text/javascript">
    import DownArrowIcon from '@/assets/icons/down-arrow.svg.vue';
    import CommonComponents from '@/components/Common.vue';

    import ApiConfig from '@/config/api.json';
    import Analytics from '@/classes/Analytics.js';

    import { UseApiStore } from '@/store/Api.js';
    import { UseAuthStore } from '@/store/Auth.js';
    import { UseUIStore } from '@/store/UI.js';

    export default {

        components: {
            DownArrowIcon,
            ...CommonComponents,
        },

        setup() {
            return {
                apiStore: UseApiStore(),
                authStore: UseAuthStore(),
                uiStore: UseUIStore(),
            };
        },
        
        data() {
            return {
                showLoginModal: false,
                loginModalOpen: true,
                loggedInUserId: null,

                disposed: false,
                requestsTimer: null,
                emptyResults: false,
                error: null,

                showOptions: false,
                
                localValue: null,
            };
        },

        props: {
            modelValue: {
                type: String,
                default: null,
            },
        },

        emits: ['search', 'update:modelValue'],

        created() {
            if (this.authStore.loggedInUser) {
                this.loggedInUserId = this.authStore.loggedInUser.id;
            }

            this.requestFresh();
            window.addEventListener('focus', this.requestFresh, {passive: true});

            this.localValue = this.modelValue;

            // we'll watch user id change
            // so we can refresh the content as needed
            this.$watch(
                () => this.authStore.loggedInUser,

                (function(newVal) {
                    if (
                        (newVal) &&
                        (newVal.id)
                    ) {
                        if (newVal.id != this.loggedInUserId) {
                            this.loggedInUserId = newVal.id;
                        }
                    }
                    else {
                        this.loggedInUserId = null;
                    }
                }.bind(this)),

                { deep: true }
            );
        },

        beforeUnmount() {
            this.disposed = true;
            clearTimeout(this.requestsTimer);
            window.removeEventListener('focus', this.requestFresh, {passive: true});
        },

        watch: {
            loggedInUserId() {
                this.requestFresh();
            },

            modelValue() {
                this.localValue = this.modelValue;
            },
        },

        computed: {

            requestUrl() {
                return 'ai/profile/options';
            },

            aiProfileData() {
                let response = this.apiStore.requests[this.requestUrl];
                
                if (!response) {
                    return null;
                }

                if (
                    (response.valid) &&
                    (response.results) &&
                    (!response.results.error)
                ) {
                    return response.results;
                }
                else {
                    let error = 'Could not complete request';
                    if ((response.results) && (response.results.error)) {
                        if (response.results.error.message) error = response.results.error.message;
                        else error = response.results.error;
                    }
                    this.setError(error);
                }
                
                return null;
            },

            aiProfileOptions() {
                if (this.aiProfileData) {
                    if (
                        (this.aiProfileData.options) &&
                        (this.aiProfileData.options.length)
                    ) {
                        this.setEmptyResults(false);
                        return this.aiProfileData.options;
                    }

                    this.setEmptyResults(true);
                }
                else {
                    this.setEmptyResults(null);
                }

                return null;
            },

            selectedOption() {
                if ((this.aiProfileOptions) && (this.modelValue)) {
                    for (let i=0; i<this.aiProfileOptions.length; i++) {
                        if (this.aiProfileOptions[i].value == this.modelValue) {
                            return this.aiProfileOptions[i];
                        }
                    }
                }

                return null;
            },

            barCssClass() {
                if (
                    (this.aiProfileOptions) &&
                    (!this.selectedOption) &&
                    (!this.showOptions)
                ) {
                    return 'bg-light';
                }
                else {
                    return 'bg-info';
                }
            },
        },

        methods: {
            closeLoginModal() {
                this.showLoginModal = false;
                this.loginModalOpen = true;
            },

            requestFresh() {
                this.requestData(true);
            },

            async requestData(fresh) {
                
                if (this.disposed) return;

                this.setEmptyResults(null);
                this.setError(null);

                let payload = {
                    url: this.requestUrl,
                };

                if (fresh) payload.fresh = true;
                
                await this.apiStore.get(payload);


                this.requestsTimer = setTimeout(this.requestData, ApiConfig.dashboardCache * 1000);

            },

            setError(error) {
                this.error = error;
            },

            setEmptyResults(val) {
                this.emptyResults = val;
            },

            goToNewProfile() {
                this.showOptions = false;
                window.open('/aesthetic-profile');
            },

            setOption(option) {
                let val = null;
                if (option) val = option.value;

                this.showOptions = false;
                this.localValue = val;
                this.$emit('update:modelValue', val);
                this.$emit('search', val);

                if (val) {
                    Analytics.event('SearchAi');
                }
            },

            checkboxClick() {
                if (this.selectedOption) {
                    this.setOption();
                }
                else {
                    this.showOptions = !this.showOptions;
                }
            },
        }
    };

</script>
